import type { Anzeigeoptionen } from '@comparit/common-api';
import { Injectable } from '@angular/core';
import { downloadBlob } from '@comparit/core';
import type { MarkedItem } from '@comparit/shared';
import type { Observable } from 'rxjs';
import { tap } from 'rxjs';
import type { VergleichResponseMap } from '../interfaces/vergleich-input.model';

/** The display options the customer toggles, as the PDF endpoint wants them. */
/** The two the page offers controls for; the app fills the rest of `Anzeigeoptionen` in. */
export type VergleichAnzeigeoptionen = Pick<
  Anzeigeoptionen,
  'leistungstexteAnzeigen' | 'nurUnterschiede'
>;

const PDF_FILE_NAME = 'Tarifvergleich.pdf';

/**
 * The Sparte seam behind the shared comparison page: the app subclass names its generated
 * `VergleichService` and `Sparte`, this lib names no generated type. Same shape as
 * `ResultsBaseService` — route literals sit here rather than in the lib, so the page hardcodes none.
 */
@Injectable()
export abstract class VergleichBaseService {
  /** The app's generated `getVergleich` call, keyed by run and then by tariff. */
  abstract readonly fetch$: (kombinationen: MarkedItem[]) => Observable<VergleichResponseMap>;

  /** The app's generated PDF call. Protected: callers go through `downloadPdf$`, which saves it. */
  protected abstract readonly generatePdf$: (
    kombinationen: MarkedItem[],
    anzeigeoptionen: VergleichAnzeigeoptionen
  ) => Observable<object>;

  /** Where the navigator's back link goes — the Sparte's results page. */
  abstract readonly backUrl: string;

  /**
   * Target of a tariff's Antrag button. Unset until the Sparte has an Antrag, and the button then
   * renders disabled — same trick as `ResultsBaseService.forwardUrl`.
   */
  readonly antragUrl?: string;

  /** The app supplies the call; the lib owns saving the blob. */
  downloadPdf$(
    kombinationen: MarkedItem[],
    anzeigeoptionen: VergleichAnzeigeoptionen
  ): Observable<object> {
    return this.generatePdf$(kombinationen, anzeigeoptionen).pipe(
      tap(blob => downloadBlob(blob as Blob, PDF_FILE_NAME))
    );
  }
}
